import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { publicApi } from "../lib/api";

const DISMISS_KEY = "cass_announce_dismissed";
const VARIANT_KEY = "cass_announce_variant";

const ss = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch {} },
};

export const AnnouncementBar = ({ announcement }) => {
  const [dismissed, setDismissed] = useState(() => ss.get(DISMISS_KEY) === "1");

  const variants = useMemo(() => {
    const v = (announcement?.variants || []).filter((x) => x && x.text);
    if (v.length) return v;
    if (announcement?.text) return [{ id: "A", text: announcement.text }];
    return [];
  }, [announcement]);

  // Choose a variant once per session (stable so impressions ↔ clicks line up).
  const variant = useMemo(() => {
    if (!variants.length) return null;
    if (!announcement?.ab_enabled || variants.length === 1) return variants[0];
    const savedId = ss.get(VARIANT_KEY);
    const found = savedId && variants.find((x) => x.id === savedId);
    if (found) return found;
    const pick = variants[Math.floor(Math.random() * variants.length)];
    ss.set(VARIANT_KEY, pick.id);
    return pick;
  }, [variants, announcement]);

  const show = !!announcement?.enabled && !!variant && !dismissed;

  // Count one impression per variant per session.
  useEffect(() => {
    if (!show || !variant) return;
    const impKey = `cass_announce_imp_${variant.id}`;
    if (ss.get(impKey) === "1") return;
    ss.set(impKey, "1");
    publicApi.trackAnnouncement(variant.id, "impression");
  }, [show, variant]);

  if (!show) return null;

  const close = () => { ss.set(DISMISS_KEY, "1"); setDismissed(true); };
  const onClick = () => publicApi.trackAnnouncement(variant.id, "click");

  return (
    <div className="bg-teal text-white" data-testid="announcement-bar" data-variant={variant.id}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex items-center justify-center gap-2 text-sm font-semibold relative">
        <Link to={announcement.link || "/legal-cashiering"} onClick={onClick} data-testid="announcement-link" className="inline-flex items-center gap-2 hover:opacity-90 transition-opacity text-center">
          <span>{variant.text}</span>
          {announcement.cta_label && <span className="inline-flex items-center gap-1 underline underline-offset-2">{announcement.cta_label} <ArrowRight size={15} /></span>}
        </Link>
        <button onClick={close} data-testid="announcement-dismiss" aria-label="Dismiss" className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
